import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import type { AuthStatus } from "./auth.types";
import { isSafeInternalPath } from "./auth.types";

const emailSchema = z
  .string()
  .trim()
  .email()
  .max(254)
  .transform((value) => value.toLowerCase());
const passwordSchema = z.string().min(8).max(128);
const nextSchema = z.string().max(300).refine(isSafeInternalPath).optional();
const optionalPhoneSchema = z
  .string()
  .trim()
  .max(40)
  .transform((value) => value || undefined)
  .optional();

export type AuthFailureReason =
  | "configuration"
  | "invalid_credentials"
  | "email_not_confirmed"
  | "rate_limit"
  | "expired_link"
  | "unknown";

function failureReason(error: { code?: string; message?: string } | null): AuthFailureReason {
  const code = error?.code ?? "";
  if (code === "invalid_credentials") return "invalid_credentials";
  if (code === "email_not_confirmed") return "email_not_confirmed";
  if (code.includes("rate_limit") || code === "over_email_send_rate_limit") return "rate_limit";
  if (code.includes("otp_expired") || code.includes("flow_state")) return "expired_link";
  return "unknown";
}

export const getAuthStatus = createServerFn({ method: "GET" }).handler(
  async (): Promise<AuthStatus> => {
    const { getVerifiedAuthStatus } = await import("./auth.server");
    return getVerifiedAuthStatus();
  },
);

export const updateOwnAccountProfile = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z
      .object({
        firstName: z.string().trim().min(1).max(80),
        lastName: z.string().trim().min(1).max(80),
        companyName: z.string().trim().max(160).optional().default(""),
        phone: optionalPhoneSchema,
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { requireAuthenticatedAccount, updateOwnAccountProfile: saveProfile } =
      await import("./auth.server");
    const account = await requireAuthenticatedAccount();
    if (account.profile?.role !== "kunde" && data.companyName.trim().length < 2) {
      throw new Error("Für ein Anbieterkonto ist der Firmenname erforderlich.");
    }
    if (
      account.profile?.role !== "kunde" &&
      account.profile?.providerStatus === "approved" &&
      data.companyName.trim() !== account.profile.companyName?.trim()
    ) {
      throw new Error(
        "Der geprüfte Firmenname kann nur durch Begner geändert werden. Bitte kontaktieren Sie info@begner.ch.",
      );
    }
    await saveProfile(account.userId, {
      firstName: data.firstName,
      lastName: data.lastName,
      companyName: data.companyName || null,
      phone: data.phone ?? null,
    });
    return { ok: true as const };
  });

export const signInProvider = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z.object({ email: emailSchema, password: z.string().min(1).max(128) }).parse(data),
  )
  .handler(async ({ data }) => {
    try {
      const { createAuthServerClient, ensureProviderProfile, getVerifiedAuthStatus } =
        await import("./auth.server");
      const client = await createAuthServerClient();
      const { data: authData, error } = await client.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error || !authData.user) {
        return { ok: false as const, reason: failureReason(error) };
      }

      await ensureProviderProfile(authData.user);
      return { ok: true as const, status: await getVerifiedAuthStatus() };
    } catch (error) {
      const { AuthConfigurationError } = await import("./auth.server");
      if (error instanceof AuthConfigurationError) {
        return { ok: false as const, reason: "configuration" as const };
      }
      console.error("Anbieter-Login fehlgeschlagen", error);
      return { ok: false as const, reason: "unknown" as const };
    }
  });

export const registerProviderAccount = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z
      .object({
        firstName: z.string().trim().min(1).max(80),
        lastName: z.string().trim().min(1).max(80),
        companyName: z.string().trim().max(160).optional().default(""),
        phone: optionalPhoneSchema,
        accountType: z.enum(["kunde", "auftragnehmer", "immobilienmakler"]),
        email: emailSchema,
        password: passwordSchema,
        termsAccepted: z.literal(true),
        next: nextSchema,
      })
      .superRefine((value, context) => {
        if (value.accountType !== "kunde" && value.companyName.trim().length < 2) {
          context.addIssue({
            code: "custom",
            path: ["companyName"],
            message: "Für ein Anbieterkonto ist der Firmenname erforderlich.",
          });
        }
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    try {
      const { authEmailRedirectUrl, createAuthServerClient, ensureProviderProfile } =
        await import("./auth.server");
      const client = await createAuthServerClient();
      const { data: authData, error } = await client.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: await authEmailRedirectUrl(data.next),
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            company_name: data.companyName || null,
            phone: data.phone ?? null,
            role: data.accountType,
          },
        },
      });

      if (error) {
        return { ok: false as const, reason: failureReason(error) };
      }

      if (authData.user) {
        try {
          await ensureProviderProfile(authData.user);
          if (data.accountType !== "kunde") {
            const { syncAccountRegistrationSubmissions } = await import("./supabase.server");
            await syncAccountRegistrationSubmissions();
          }
        } catch (profileError) {
          // The account exists already; a later confirmed login repairs a missing profile.
          console.error("Profilanlage nach Registrierung fehlgeschlagen", profileError);
        }
      }

      // Supabase deliberately obscures whether an email address already exists.
      return { ok: true as const, confirmationRequired: !authData.session };
    } catch (error) {
      const { AuthConfigurationError } = await import("./auth.server");
      if (error instanceof AuthConfigurationError) {
        return { ok: false as const, reason: "configuration" as const };
      }
      console.error("Anbieter-Registrierung fehlgeschlagen", error);
      return { ok: false as const, reason: "unknown" as const };
    }
  });

export const resendAccountConfirmation = createServerFn({ method: "POST" })
  .validator((data: unknown) => z.object({ email: emailSchema, next: nextSchema }).parse(data))
  .handler(async ({ data }) => {
    try {
      const { authEmailRedirectUrl, createAuthServerClient } = await import("./auth.server");
      const client = await createAuthServerClient();
      const { error } = await client.auth.resend({
        type: "signup",
        email: data.email,
        options: { emailRedirectTo: await authEmailRedirectUrl(data.next) },
      });
      return error ? { ok: false as const, reason: failureReason(error) } : { ok: true as const };
    } catch (error) {
      const { AuthConfigurationError } = await import("./auth.server");
      if (error instanceof AuthConfigurationError) {
        return { ok: false as const, reason: "configuration" as const };
      }
      return { ok: false as const, reason: "unknown" as const };
    }
  });

export const completeAuthCallback = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z
      .object({
        code: z.string().min(10).max(2048),
        flowId: z.string().max(2048).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    try {
      const { createAuthServerClient, ensureProviderProfile, getVerifiedAuthStatus } =
        await import("./auth.server");
      const client = await createAuthServerClient();
      const { data: authData, error } = await client.auth.exchangeCodeForSession(
        data.code,
        data.flowId ? { flowId: data.flowId } : undefined,
      );

      if (error || !authData.user) {
        return { ok: false as const, reason: failureReason(error) };
      }

      await ensureProviderProfile(authData.user);
      return { ok: true as const, status: await getVerifiedAuthStatus() };
    } catch (error) {
      const { AuthConfigurationError } = await import("./auth.server");
      if (error instanceof AuthConfigurationError) {
        return { ok: false as const, reason: "configuration" as const };
      }
      console.error("E-Mail-Bestätigung fehlgeschlagen", error);
      return { ok: false as const, reason: "unknown" as const };
    }
  });

export const signOutProvider = createServerFn({ method: "POST" }).handler(async () => {
  try {
    const { createAuthServerClient } = await import("./auth.server");
    const client = await createAuthServerClient();
    const { error } = await client.auth.signOut();
    if (error) {
      console.error("Abmelden bei Supabase fehlgeschlagen", error);
      return { ok: false as const };
    }
  } catch (error) {
    console.error("Anbieter-Logout fehlgeschlagen", error);
    return { ok: false as const };
  }
  return { ok: true as const };
});
